import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Recycle, FileText, X, Check, Ruler, Layers, Palette, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import type { ColorVariant } from "@/pages/Produits";

interface ProductSpecifications {
  dimensions?: string;
  epaisseur?: string;
  couleur?: string;
  personnalisation?: boolean;
}

interface Product {
  id: number;
  name: string;
  category: string;
  description: string;
  image: string;
  tags: string[];
  sizes: string[];
  colors?: ColorVariant[];
  sizeImages?: Record<string, string>;
  specifications?: ProductSpecifications;
}

interface ProductModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProductModal = ({ product, isOpen, onClose }: ProductModalProps) => {
  const [selectedColor, setSelectedColor] = useState<ColorVariant | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);

  useEffect(() => {
    if (product?.colors && product.colors.length > 0) {
      setSelectedColor(product.colors[0]);
    } else {
      setSelectedColor(null);
    }
    if (product?.sizeImages && product.sizes.length > 0) {
      setSelectedSize(product.sizes[0]);
    } else {
      setSelectedSize(null);
    }
  }, [product]);

  if (!product) return null;

  const displayImage = selectedColor 
    ? selectedColor.image 
    : (selectedSize && product.sizeImages) 
      ? product.sizeImages[selectedSize] 
      : product.image;
  const colorName = selectedColor ? ` - ${selectedColor.name}` : "";
  const sizeName = selectedSize ? ` - ${selectedSize}` : "";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full bg-background/80 p-2 hover:bg-background transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        
        <div className="grid md:grid-cols-2 gap-0">
          {/* Image Section */}
          <div className="relative h-64 md:h-full bg-gradient-to-br from-secondary/50 to-white flex items-center justify-center p-8">
            <img
              src={displayImage}
              alt={product.name}
              className="max-w-full max-h-64 object-contain animate-scale-in"
            />
            <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
              {product.category}
            </Badge>
          </div>

          {/* Content Section */}
          <div className="p-6 flex flex-col">
            <DialogHeader className="text-left mb-4">
              <DialogTitle className="font-display text-2xl font-bold text-accent">
                {product.name}
              </DialogTitle>
            </DialogHeader>

            <p className="text-muted-foreground mb-6 leading-relaxed">
              {product.description}
            </p>

            {/* Colors */}
            {product.colors && product.colors.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground mb-2">
                  Couleur : {selectedColor?.name}
                </h4>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((color) => (
                    <button
                      key={color.name}
                      onClick={() => setSelectedColor(color)}
                      className={`relative w-10 h-10 rounded-full ${color.colorClass} border-2 transition-all ${
                        selectedColor?.name === color.name
                          ? "border-primary ring-2 ring-primary/30 scale-110"
                          : "border-border hover:scale-105"
                      }`}
                      title={color.name}
                    >
                      {selectedColor?.name === color.name && (
                        <Check className="absolute inset-0 m-auto w-5 h-5 text-white" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sizes */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-foreground mb-2">
                Tailles disponibles{selectedSize ? ` : ${selectedSize}` : ""}
              </h4>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => product.sizeImages && setSelectedSize(size)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      product.sizeImages 
                        ? selectedSize === size
                          ? "bg-primary text-primary-foreground ring-2 ring-primary/30"
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer"
                        : "bg-secondary text-secondary-foreground"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Specifications */}
            {product.specifications && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground mb-3">
                  Spécifications techniques
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {product.specifications.dimensions && (
                    <div className="flex items-center gap-2 p-2.5 bg-secondary/50 rounded-lg">
                      <Ruler className="w-4 h-4 text-primary shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Dimensions</p>
                        <p className="text-sm font-medium text-foreground">{product.specifications.dimensions}</p>
                      </div>
                    </div>
                  )}
                  {product.specifications.epaisseur && (
                    <div className="flex items-center gap-2 p-2.5 bg-secondary/50 rounded-lg">
                      <Layers className="w-4 h-4 text-primary shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Épaisseur</p>
                        <p className="text-sm font-medium text-foreground">{product.specifications.epaisseur}</p>
                      </div>
                    </div>
                  )}
                  {product.specifications.couleur && (
                    <div className="flex items-center gap-2 p-2.5 bg-secondary/50 rounded-lg">
                      <Palette className="w-4 h-4 text-primary shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Couleur</p>
                        <p className="text-sm font-medium text-foreground">{product.specifications.couleur}</p>
                      </div>
                    </div>
                  )}
                  {product.specifications.personnalisation && (
                    <div className="flex items-center gap-2 p-2.5 bg-eco/10 rounded-lg">
                      <Sparkles className="w-4 h-4 text-eco shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Personnalisation</p>
                        <p className="text-sm font-medium text-eco">Disponible</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tags */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-foreground mb-2">
                Caractéristiques
              </h4>
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-eco/10 text-eco rounded-full text-sm font-medium"
                  >
                    <Recycle className="w-3.5 h-3.5" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-auto pt-4 border-t border-border">
              <Button variant="kraft" size="lg" className="w-full" asChild>
                <Link to={`/devis?produit=${encodeURIComponent(product.name + colorName + sizeName)}`}>
                  <FileText className="w-4 h-4" />
                  Demander un devis
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;